// src/pages/Client/LeaveReviewPage.js
// Page pour laisser un avis après un RDV terminé

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import reviewService from '../../services/reviewService';

// ============================================================
// NAVIGATION DEPUIS ReservationDetailModal — CORRIGER EN :
//
//   const handleReview = () => {
//     navigate('/review', {
//       state: {
//         reservationId: reservation.id,
//         salonName: reservation.salon_name,
//         serviceName: getServiceDisplay(reservation), // fonction déjà dispo dans le modal
//         reservationDate: reservation.reservation_date
//       }
//     });
//     onClose();
//   };
//
// Et dans App.js ajouter :
//   <Route path="/review" element={<LeaveReviewPage />} />
// ============================================================

const LeaveReviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Données passées depuis ReservationDetailModal
  const { reservationId, salonName, serviceName, reservationDate } = location.state || {};

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Rediriger si on arrive sans données
  if (!reservationId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>❌ Données manquantes</h2>
        <p>Impossible d'accéder à cette page directement.</p>
        <button onClick={() => navigate('/reservations')}>
          Retour à mes réservations
        </button>
      </div>
    );
  }

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Soumettre l'avis
  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Veuillez sélectionner une note');
      return;
    }

    setLoading(true);
    setError('');

    const result = await reviewService.createReview({
      reservationId,
      rating,
      comment: comment.trim() || null
    });

    if (result.success) {
      setSuccess(true);
      // Redirection vers mes réservations après 2s
      setTimeout(() => navigate('/reservations'), 2000);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  // Labels des étoiles
  const ratingLabels = {
    1: 'Mauvais',
    2: 'Passable',
    3: 'Bien',
    4: 'Très bien',
    5: 'Excellent'
  };

  if (success) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '64px' }}>✅</div>
        <h2>Merci pour votre avis !</h2>
        <p>Votre avis a été publié avec succès.</p>
        <p style={{ color: '#888' }}>Redirection en cours...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '24px' }}>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/reservations')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', marginBottom: '12px' }}
        >
          ← Retour
        </button>
        <h1 style={{ margin: '0 0 4px' }}>Laisser un avis</h1>
        <p style={{ margin: 0, color: '#666' }}>Partagez votre expérience</p>
      </div>

      {/* Infos RDV */}
      <div style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        background: '#f9f9f9'
      }}>
        <p style={{ margin: '0 0 4px', fontWeight: '600' }}>{salonName}</p>
        {serviceName && <p style={{ margin: '0 0 4px', color: '#555' }}>✂️ {serviceName}</p>}
        {reservationDate && <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>📅 {formatDate(reservationDate)}</p>}
      </div>

      {/* Sélection étoiles */}
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontWeight: '600', marginBottom: '12px' }}>Votre note *</p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '36px',
                padding: '0',
                color: star <= (hoveredRating || rating) ? '#FCD34D' : '#D1D5DB',
                transition: 'color 0.1s'
              }}
            >
              ★
            </button>
          ))}
        </div>
        {(hoveredRating || rating) > 0 && (
          <p style={{ margin: 0, color: '#555', fontSize: '14px' }}>
            {ratingLabels[hoveredRating || rating]}
          </p>
        )}
      </div>

      {/* Commentaire */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>
          Commentaire (optionnel)
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Décrivez votre expérience : accueil, qualité du service, ambiance..."
          maxLength={1000}
          rows={5}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />
        <p style={{ margin: '4px 0 0', color: '#888', fontSize: '12px', textAlign: 'right' }}>
          {comment.length}/1000
        </p>
      </div>

      {/* Erreur */}
      {error && (
        <div style={{
          background: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          color: '#DC2626'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={() => navigate('/reservations')}
          disabled={loading}
          style={{
            flex: 1,
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: 'white',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Annuler
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || rating === 0}
          style={{
            flex: 2,
            padding: '12px',
            border: 'none',
            borderRadius: '8px',
            background: rating === 0 ? '#D1D5DB' : '#B76E79',
            color: 'white',
            cursor: rating === 0 ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          {loading ? 'Publication...' : '⭐ Publier mon avis'}
        </button>
      </div>

    </div>
  );
};

export default LeaveReviewPage;